import React, { useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { masterInvoices } from '../services/masterApi';
import LoadingSpinner from '../components/LoadingSpinner';

export default function MasterInvoices() {
  const [masterId] = useState(() => {
    const token = localStorage.getItem('masterToken');
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return decoded.id;
      } catch {
        return null;
      }
    }
    return null;
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const filters = {
    status: filterStatus !== 'all' ? filterStatus : undefined,
    dateRange: filterDateRange !== 'all' ? filterDateRange : undefined,
  };

  const { data: invoicesData, isLoading, refetch } = useQuery(
    ['masterInvoices', masterId, filters],
    () => masterInvoices.getAll(masterId, filters),
    { enabled: !!masterId, retry: 1 }
  );

  const invoices = invoicesData?.data?.invoices || [];

  const filteredInvoices = invoices.filter((invoice) =>
    invoice.invoiceId.toString().includes(searchTerm) ||
    invoice.buyerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.sellerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const handleSyncFBR = async (invoiceId) => {
    try {
      const result = await masterInvoices.syncWithFBR(invoiceId);
      if (result.success) {
        toast.success('Invoice synced with FBR');
        refetch();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to sync invoice');
    }
  };

  const handleResubmit = async (invoiceId) => {
    try {
      const result = await masterInvoices.resubmit(invoiceId);
      if (result.success) {
        toast.success('Invoice resubmitted');
        refetch();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to resubmit invoice');
    }
  };

  if (!masterId) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Unable to load invoices. Please login again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-2">Manage all invoices created by your users</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Invoice ID, Buyer, or Seller..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <select
                value={filterDateRange}
                onChange={(e) => setFilterDateRange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => refetch()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        {isLoading ? (
          <LoadingSpinner />
        ) : filteredInvoices.length > 0 ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Invoice ID</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Seller</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Buyer</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">Amount</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">
                      #{invoice.invoiceId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {invoice.sellerName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {invoice.buyerName}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                      Rs. {invoice.totalAmount?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <StatusBadge status={invoice.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(invoice.createdDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-center space-x-2">
                      <button
                        onClick={() => handleViewDetails(invoice)}
                        className="text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        View
                      </button>
                      {invoice.status === 'draft' && (
                        <button
                          onClick={() => handleSyncFBR(invoice.invoiceId)}
                          className="text-green-600 hover:text-green-800 font-semibold"
                        >
                          Sync
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'No invoices found matching your search.' : 'No invoices available.'}
            </p>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedInvoice && (
          <InvoiceDetailModal
            invoice={selectedInvoice}
            onClose={() => setShowDetailModal(false)}
            onSync={() => handleSyncFBR(selectedInvoice.invoiceId)}
            onResubmit={() => handleResubmit(selectedInvoice.invoiceId)}
          />
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    submitted: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status] || statusColors.draft}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

function InvoiceDetailModal({ invoice, onClose, onSync, onResubmit }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Invoice #{invoice.invoiceId}</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-lg p-2 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Seller</p>
              <p className="text-lg font-semibold text-gray-900">{invoice.sellerName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Buyer</p>
              <p className="text-lg font-semibold text-gray-900">{invoice.buyerName}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Amount</p>
              <p className="text-2xl font-bold text-gray-900">Rs. {invoice.totalAmount?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <StatusBadge status={invoice.status} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Created Date</p>
              <p className="text-gray-900">{new Date(invoice.createdDate).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Scenario</p>
              <p className="text-gray-900">{invoice.scenarioName}</p>
            </div>
          </div>

          {invoice.items && invoice.items.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Line Items</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-right">Qty</th>
                      <th className="px-4 py-2 text-right">Unit Price</th>
                      <th className="px-4 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-200 last:border-b-0">
                        <td className="px-4 py-2">{item.description}</td>
                        <td className="px-4 py-2 text-right">{item.quantity}</td>
                        <td className="px-4 py-2 text-right">Rs. {item.unitPrice?.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right font-semibold">Rs. {item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded transition-colors"
          >
            Close
          </button>
          {invoice.status === 'draft' && (
            <button
              onClick={onSync}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
            >
              Sync with FBR
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
